import { type ComponentType, type FC } from "react";
import { SelectBox } from "@root/shared/components/SelectBox";
import { components, type OptionProps } from "react-select";
import { SearchableSelectBox } from "@root/shared/components/SearchableSelectBox";
import { PopOver } from "@root/shared/components/PopOver";
import { SpLayout } from "./uiParts/SpLayout";
import { PcLayout } from "./uiParts/PcLayout";
import {
  useWorksFilter,
  type Work,
  type WorkOption,
  type WorkGroupedOptions,
} from "./useWorksFilter";
import * as styles from "./styles.css";
import { AutoSelector } from "./uiParts/AutoSelector";
import { type AutoSelectStatus } from "../ScoutTargetAndWorkSelector/useAutoSelectWork";

// おまかせ求人選択の状態とハンドラ（isAutoSelectorVisible が true の画面のみ渡す）
export type AutoSelectProps = {
  status: AutoSelectStatus;
  onAutoSelect: () => void;
  onSelfSelect: () => void;
};

export type WorkSelectorProps = {
  isSp: boolean;
  isRequired?: boolean;
  totalWorksCount: number;
  currentWorkId: string | undefined;
  onWorkChange: (workId: string | undefined) => void;
  works: readonly Work[];
  isAutoSelectorVisible: boolean;
  autoSelectDisabledMessage?: string;
  autoSelect?: AutoSelectProps;
  hasBorder?: boolean;
  isDescriptionVisible?: boolean;
  recommendText?: string;
  hasMatchingJobs: boolean;
};

export const WorkSelector: FC<WorkSelectorProps> = ({
  isSp,
  isRequired,
  totalWorksCount,
  currentWorkId,
  onWorkChange,
  works,
  isAutoSelectorVisible,
  autoSelectDisabledMessage,
  autoSelect,
  hasBorder,
  isDescriptionVisible,
  recommendText,
  hasMatchingJobs,
}) => {
  const {
    selectableWorksCount,
    workOptions,
    brandOptions,
    prefectureOptions,
    businessTypeOptions,
    occupationOptions,
    isVisible: isFiltersVisible,
    handleChangeCondition,
    handleToggleFilter,
  } = useWorksFilter(works, currentWorkId, onWorkChange);

  const Layout = isSp ? SpLayout : PcLayout;

  return (
    <>
      {isAutoSelectorVisible && autoSelect && (
        <div className={styles.autoSelectorContainer}>
          <AutoSelector
            currentWorkId={currentWorkId}
            autoSelectStatus={autoSelect.status}
            onAutoSelect={autoSelect.onAutoSelect}
            onSelfSelect={autoSelect.onSelfSelect}
            autoSelectDisabledMessage={autoSelectDisabledMessage}
            isSp={isSp}
          />
        </div>
      )}
      {currentWorkId !== "auto" && (
        <Layout
          isRequired={isRequired}
          toggleButtonText={isFiltersVisible ? "絞り込みを閉じる" : "絞り込みを開く"}
          onToggleButtonClick={handleToggleFilter}
          selectableWorksCount={selectableWorksCount}
          totalWorksCount={totalWorksCount}
          toggleButtonClassName={styles.toggleButton({
            isFiltersVisible,
          })}
          hasBorder={hasBorder}
          isDescriptionVisible={isDescriptionVisible}
          recommendText={recommendText}
        >
          {isFiltersVisible && (
            <div className={styles.selectorsOuter}>
              <SelectBox
                selected
                options={brandOptions}
                placeholder="すべてのブランド"
                onChange={(event) => handleChangeCondition(event.target.value, "brands")}
                role="combobox"
                aria-label="ブランドを選択する"
                className={styles.selectBox}
              />
              <SelectBox
                selected
                options={prefectureOptions}
                placeholder="すべての都道府県"
                onChange={(event) => handleChangeCondition(event.target.value, "prefectures")}
                role="combobox"
                aria-label="都道府県を選択する"
                className={styles.selectBox}
              />
              <SelectBox
                selected
                options={businessTypeOptions}
                placeholder="すべての職種"
                onChange={(event) => handleChangeCondition(event.target.value, "businessTypes")}
                role="combobox"
                aria-label="職種を選択する"
                className={styles.selectBox}
              />
              <SelectBox
                selected
                options={occupationOptions}
                placeholder="すべての役職・採用対象"
                onChange={(event) => handleChangeCondition(event.target.value, "occupations")}
                role="combobox"
                aria-label="役職を選択する"
                className={styles.selectBox}
              />
            </div>
          )}
          <div id="js-work-selector-outer" className={styles.searchSelectBoxOuter}>
            <SearchableSelectBox
              value={currentWorkId}
              options={workOptions}
              onChange={onWorkChange}
              placeholder="求人を選択する（入力して絞り込むこともできます）"
              noOptionsMessage="条件に一致する求人がありません"
              customOption={CustomOption}
              aria-label="求人を選択する"
              instanceId="work-selector"
            />
            {hasMatchingJobs && (
              <PopOver arrowPosition="topLeft" className={styles.popOver}>
                <p className={styles.popOverInner}>
                  求職者の希望・経験とマッチする求人を選択できます
                </p>
              </PopOver>
            )}
          </div>
        </Layout>
      )}
    </>
  );
};

const MAX_SHOW_SHOP_COUNT = 3;

const CustomOption: ComponentType<OptionProps<WorkOption, false, WorkGroupedOptions>> = (props) => {
  const {
    data: { shops, label },
  } = props;
  return (
    <components.Option {...props}>
      <div className={styles.optionInner}>
        <span>{label}</span>
        <span className={styles.shopNameOuter}>
          {shops.slice(0, MAX_SHOW_SHOP_COUNT).map((shopName) => (
            <span key={`${label}_${shopName}`} className={styles.shopName}>
              {shopName}
            </span>
          ))}
          {shops.length > MAX_SHOW_SHOP_COUNT && <>...他{shops.length - MAX_SHOW_SHOP_COUNT}店舗</>}
        </span>
      </div>
    </components.Option>
  );
};
